import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Fleet Module Service Stores
 * Services for fleet management including drivers, trucks, inspections, and maintenance
 */
export const fleetStores: ServiceStoreConfig[] = [
  {
    name: "drivers",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.driversStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalDrivers = entities.length;
          const activeDrivers = entities.filter(
            (driver: Record<string, unknown>) =>
              driver.status === "active" || driver.isActive === true
          ).length;
          const availableDrivers = entities.filter(
            (driver: Record<string, unknown>) =>
              driver.status === "available"
          ).length;
          const onDutyDrivers = entities.filter(
            (driver: Record<string, unknown>) =>
              driver.status === "on_duty" || driver.status === "driving"
          ).length;

          return [
            {
              title: "Total Drivers",
              value: () => totalDrivers.toString(),
              valueColor: "primary.main",
              variant: "with-icon" as const,
            },
            {
              title: "Active",
              value: () => activeDrivers.toString(),
              valueColor: "success.main",
            },
            {
              title: "Available",
              value: () => availableDrivers.toString(),
              valueColor: "info.main",
            },
            {
              title: "On Duty",
              value: () => onDutyDrivers.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "trucks",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.trucksStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalTrucks = entities.length;
          const availableTrucks = entities.filter(
            (truck: Record<string, unknown>) =>
              truck.status === "available"
          ).length;
          const inUseTrucks = entities.filter(
            (truck: Record<string, unknown>) =>
              truck.status === "in_use" || truck.status === "dispatched"
          ).length;
          const maintenanceTrucks = entities.filter(
            (truck: Record<string, unknown>) =>
              truck.status === "maintenance" || truck.status === "repair"
          ).length;

          return [
            {
              title: "Total Trucks",
              value: () => totalTrucks.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Available",
              value: () => availableTrucks.toString(),
              valueColor: "success.main",
            },
            {
              title: "In Use",
              value: () => inUseTrucks.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Maintenance",
              value: () => maintenanceTrucks.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
        actions: {
          onRowClick: (row) => {
            console.log("View truck:", row.truckId);
          },
        },
      },
    },
  },
  {
    name: "truck-inspections",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.truckInspectionsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalInspections = entities.length;
          const todayInspections = entities.filter(
            (inspection: Record<string, unknown>) => {
              const inspectionDate = new Date(inspection.createdAt as string);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return inspectionDate >= today;
            }
          ).length;
          const passedInspections = entities.filter(
            (inspection: Record<string, unknown>) =>
              inspection.result === "passed" || inspection.status === "approved"
          ).length;
          const failedInspections = entities.filter(
            (inspection: Record<string, unknown>) =>
              inspection.result === "failed" || inspection.status === "rejected"
          ).length;

          return [
            {
              title: "Total Inspections",
              value: () => totalInspections.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Today",
              value: () => todayInspections.toString(),
              valueColor: "info.main",
            },
            {
              title: "Passed",
              value: () => passedInspections.toString(),
              valueColor: "success.main",
            },
            {
              title: "Failed",
              value: () => failedInspections.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
        defaultSort: {
          sortBy: ["createdAt"],
          sortDirection: { createdAt: "DESC" },
        },
      },
    },
  },
  {
    name: "truck-maintenance",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.truckMaintenanceStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalMaintenance = entities.length;
          const scheduledMaintenance = entities.filter(
            (maintenance: Record<string, unknown>) =>
              maintenance.status === "scheduled"
          ).length;
          const inProgressMaintenance = entities.filter(
            (maintenance: Record<string, unknown>) =>
              maintenance.status === "in_progress"
          ).length;
          const completedMaintenance = entities.filter(
            (maintenance: Record<string, unknown>) =>
              maintenance.status === "completed"
          ).length;

          return [
            {
              title: "Total Records",
              value: () => totalMaintenance.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Scheduled",
              value: () => scheduledMaintenance.toString(),
              valueColor: "info.main",
            },
            {
              title: "In Progress",
              value: () => inProgressMaintenance.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Completed",
              value: () => completedMaintenance.toString(),
              valueColor: "success.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
        defaultSort: {
          sortBy: ["scheduledDate"],
          sortDirection: { scheduledDate: "ASC" },
        },
      },
    },
  },
];